// Station detail views for R2: lightweight single-line diagram (no external dep),
// inverter matrix, per-inverter string grid, КТП panel.
import { useState } from 'react';
import { scada, stateColor, type OpState } from '@/scada/tokens';
import type { StationConfig } from '@/scada/mock/stations';
import type { StationTelemetry } from '@/scada/mock/telemetry';
import type { StationDetail, InverterUnit } from '@/scada/mock/stationDetail';

const fmtKw = (kw: number) => (kw >= 1000 ? `${(kw / 1000).toFixed(2)} МВт` : `${Math.round(kw)} кВт`);
const dot = (s: OpState) => <span style={{ width: 8, height: 8, borderRadius: 999, background: stateColor(s), display: 'inline-block' }} />;

function Node({ title, lines, state, icon }: { title: string; lines: { k: string; v: string }[]; state: OpState; icon: string }) {
  const c = stateColor(state);
  return (
    <div style={{ background: scada.panel, border: `1px solid ${c}55`, borderTop: `3px solid ${c}`, borderRadius: 10, padding: '8px 12px', minWidth: 150 }}>
      <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
        <i className={icon} style={{ color: c, fontSize: 14 }} />
        <span style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>{title}</span>
      </div>
      {lines.map((l) => (
        <div key={l.k} className="flex items-center justify-between" style={{ gap: 12 }}>
          <span style={{ color: scada.textMuted, fontSize: 10 }}>{l.k}</span>
          <span style={{ color: scada.textDim, fontSize: 11, fontWeight: 500 }}>{l.v}</span>
        </div>
      ))}
    </div>
  );
}

const vline = (h: number) => <div style={{ width: 2, height: h, background: scada.borderStrong }} />;

// ─── Single-line diagram ─────────────────────────────────────────────────────
export function Sld({ cfg, t, detail }: { cfg: StationConfig; t: StationTelemetry; detail: StationDetail }) {
  return (
    <div className="flex flex-col items-center" style={{ padding: '8px 4px', overflowX: 'auto' }}>
      <Node title="Точка приєднання" state={t.state === 'offline' ? 'offline' : 'ok'} icon="ri-plug-line"
        lines={[
          { k: 'P', v: fmtKw(t.activePowerKw) },
          { k: 'Ліміт', v: t.activeLimitKw != null ? fmtKw(t.activeLimitKw) : '—' },
          { k: 'f', v: `${t.freqHz} Гц` },
        ]} />
      {vline(18)}
      {/* MV bus bar */}
      <div style={{ height: 3, background: scada.borderStrong, borderRadius: 2, width: 'min(100%, ' + (detail.ktps.length + (cfg.hasBess ? 1 : 0)) * 180 + 'px)' }} />
      <div className="flex items-start" style={{ gap: 24, marginTop: 0 }}>
        {detail.ktps.map((k) => {
          const kids = detail.inverters.filter((inv) => inv.ktpId === k.id);
          const working = kids.filter((inv) => inv.status !== 'offline' && inv.status !== 'alarm').length;
          const sumKw = kids.reduce((s, inv) => s + inv.pacKw, 0);
          const grpState: OpState = kids.some((inv) => inv.status === 'alarm') ? 'warning' : k.status;
          return (
            <div key={k.id} className="flex flex-col items-center">
              {vline(16)}
              <Node title={k.name} state={k.status} icon="ri-archive-2-line"
                lines={[
                  { k: 'U', v: `${k.voltageKv} кВ` },
                  { k: 'I', v: `${k.currentA} А` },
                  { k: 'T', v: `${k.tempC} °C` },
                ]} />
              {vline(12)}
              <Node title="Інвертори" state={grpState} icon="ri-cpu-line"
                lines={[
                  { k: 'В роботі', v: `${working}/${kids.length}` },
                  { k: 'P', v: fmtKw(sumKw) },
                ]} />
            </div>
          );
        })}
        {cfg.hasBess && t.bess && (
          <div className="flex flex-col items-center">
            {vline(16)}
            <Node title="УЗЕ / BESS" state={t.bess.mode === 'fault' ? 'alarm' : 'ok'} icon="ri-battery-2-charge-line"
              lines={[
                { k: 'SOC', v: `${t.bess.socPct}%` },
                { k: 'P', v: `${Math.abs(t.bess.powerKw)} кВт` },
                { k: 'Режим', v: t.bess.mode === 'charging' ? 'заряд' : t.bess.mode === 'discharging' ? 'розряд' : 'очікує' },
              ]} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Inverter matrix ─────────────────────────────────────────────────────────
export function InverterMatrix({ detail }: { detail: StationDetail }) {
  const [sel, setSel] = useState<InverterUnit | null>(null);
  return (
    <div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))' }}>
        {detail.inverters.map((inv) => {
          const c = stateColor(inv.status);
          return (
            <button key={inv.id} onClick={() => setSel(inv)}
              style={{ textAlign: 'left', background: scada.panel2, border: `1px solid ${inv.id === sel?.id ? scada.accent : c + '40'}`, borderLeft: `3px solid ${c}`, borderRadius: 8, padding: '7px 9px', cursor: 'pointer' }}>
              <div className="flex items-center justify-between">
                <span style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>{inv.id}</span>
                {dot(inv.status)}
              </div>
              <div style={{ color: scada.textDim, fontSize: 11, marginTop: 2 }}>{fmtKw(inv.pacKw)}</div>
              <div className="flex items-center justify-between" style={{ marginTop: 2 }}>
                <span style={{ color: scada.textMuted, fontSize: 10 }}>{inv.tempC ? `${inv.tempC}°C` : '—'}</span>
                <span style={{ color: inv.stringsFault ? scada.state.warning : scada.textMuted, fontSize: 10 }}>
                  стрінги {inv.strings - inv.stringsFault}/{inv.strings}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {sel && <StringGrid inv={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

// ─── Per-inverter string grid (ATS PV-string style) ──────────────────────────
function StringGrid({ inv, onClose }: { inv: InverterUnit; onClose: () => void }) {
  return (
    <div style={{ marginTop: 14, background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 10, padding: 12 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>
          {inv.id} · стрінги ({inv.strings - inv.stringsFault}/{inv.strings} в нормі)
        </span>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: scada.textMuted, cursor: 'pointer', fontSize: 16 }}><i className="ri-close-line" /></button>
      </div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))' }}>
        {Array.from({ length: inv.strings }).map((_, i) => {
          const fault = i >= inv.strings - inv.stringsFault;
          const c = inv.status === 'offline' ? scada.state.offline : fault ? scada.state.alarm : scada.state.ok;
          return (
            <div key={i} style={{ background: `${c}1A`, border: `1px solid ${c}55`, borderRadius: 6, padding: '5px 4px', textAlign: 'center' }}>
              <div style={{ color: scada.textDim, fontSize: 9 }}>S{String(i + 1).padStart(2, '0')}</div>
              <div style={{ color: c, fontSize: 10, fontWeight: 600 }}>{inv.status === 'offline' ? '—' : fault ? 'AVR' : `${(7 + Math.random()).toFixed(1)}A`}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── КТП panel ───────────────────────────────────────────────────────────────
export function KtpPanel({ detail }: { detail: StationDetail }) {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
      {detail.ktps.map((k) => {
        const c = stateColor(k.status);
        const kids = detail.inverters.filter((inv) => inv.ktpId === k.id);
        const working = kids.filter((inv) => inv.status !== 'offline' && inv.status !== 'alarm').length;
        return (
          <div key={k.id} style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderTop: `3px solid ${c}`, borderRadius: 10, padding: 12 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
              <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{k.name}</span>
              <span className="inline-flex items-center gap-1.5">{dot(k.status)}<span style={{ color: c, fontSize: 11 }}>{k.alarms ? 'аварія' : 'норма'}</span></span>
            </div>
            {[
              { k: 'Напруга секції', v: `${k.voltageKv} кВ` },
              { k: 'Струм', v: `${k.currentA} А` },
              { k: 'Темп. трансформатора', v: `${k.tempC} °C` },
              { k: 'Інвертори в роботі', v: `${working}/${kids.length}` },
            ].map((r) => (
              <div key={r.k} className="flex items-center justify-between" style={{ padding: '2px 0' }}>
                <span style={{ color: scada.textMuted, fontSize: 11 }}>{r.k}</span>
                <span style={{ color: scada.textDim, fontSize: 12, fontWeight: 500 }}>{r.v}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
